/* eslint-disable react/no-unescaped-entities */
/**
 * Pages SEO « Avocat droit du X » / « Expert-comptable X » / « Notaire X ».
 *
 * 17 landings spécialisées par domaine d'intervention.
 * URL : /avocat-droit-du-travail, /expert-comptable-sci, /notaire-succession, etc.
 */
import { Link, useParams } from "react-router-dom";
import {
  Compass, ScanLine, GitMerge, ShieldCheck, ArrowRight,
  Briefcase, Scale, Home as HomeIcon, Users, BookOpen, BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Seo, { ldBreadcrumb, ldFAQ } from "@/components/Seo";


// ----------------------------------------------------------------------------
// 17 landings spécialisées
// ----------------------------------------------------------------------------
export const SPECIALTY_PAGES = {
  // -- Avocats (6)
  "avocat-droit-du-travail": {
    parent: "avocat",
    label: "Avocat en droit du travail",
    h1: "Trouver un avocat en droit du travail",
    seo_title: "Avocat droit du travail — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat en droit du travail (licenciement, rupture conventionnelle, prud'hommes, harcèlement, discrimination). Orientation institutionnelle CONFILEX.",
    intent: ["avocat droit du travail", "avocat prud'hommes", "avocat licenciement"],
    cases: ["Licenciement abusif ou contesté", "Rupture conventionnelle", "Conflit avec l'employeur", "Harcèlement / discrimination", "Heures supplémentaires impayées", "Procédure prud'homale"],
    docs: ["Contrat de travail et avenants", "Bulletins de paie", "Lettre de licenciement / convocation", "Courriels et notifications RH", "Convention collective applicable"],
  },
  "avocat-immobilier": {
    parent: "avocat",
    label: "Avocat en droit immobilier",
    h1: "Trouver un avocat en droit immobilier",
    seo_title: "Avocat immobilier — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat en droit immobilier (vente, copropriété, location, troubles, urbanisme). Orientation institutionnelle CONFILEX.",
    intent: ["avocat immobilier", "avocat copropriété", "avocat bail commercial"],
    cases: ["Litige de vente immobilière", "Trouble de jouissance", "Conflit de copropriété", "Bail commercial / habitation", "Urbanisme & permis", "Trouble du voisinage"],
    docs: ["Compromis ou acte de vente", "Bail et état des lieux", "PV d'assemblée générale", "Diagnostics", "Courriers recommandés"],
  },
  "avocat-fiscal": {
    parent: "avocat",
    label: "Avocat fiscaliste",
    h1: "Trouver un avocat fiscaliste",
    seo_title: "Avocat fiscaliste — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat fiscaliste (contentieux fiscal, contrôle, rectification, optimisation patrimoniale). Orientation institutionnelle CONFILEX.",
    intent: ["avocat fiscal", "avocat fiscaliste", "contrôle fiscal avocat"],
    cases: ["Contrôle fiscal / proposition de rectification", "Contentieux URSSAF", "Optimisation patrimoniale", "Holding familiale", "Investissement immobilier", "Fiscalité internationale"],
    docs: ["Proposition de rectification", "Avis d'imposition", "Déclarations fiscales", "Statuts et K-bis", "Courriers DGFiP"],
  },
  "avocat-societes": {
    parent: "avocat",
    label: "Avocat en droit des sociétés",
    h1: "Trouver un avocat en droit des sociétés",
    seo_title: "Avocat droit des sociétés — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat en droit des sociétés (constitution, cession, pacte d'associés, gouvernance, M&A). Orientation institutionnelle CONFILEX.",
    intent: ["avocat droit des sociétés", "avocat M&A", "avocat pacte associés"],
    cases: ["Constitution de société", "Cession de parts / fonds de commerce", "Pacte d'associés", "Conflit entre associés", "Augmentation de capital", "M&A / opérations transfrontalières"],
    docs: ["Statuts et K-bis", "Pacte d'associés", "PV d'AG", "Comptes annuels", "Lettre d'intention / LOI"],
  },
  "avocat-commercial": {
    parent: "avocat",
    label: "Avocat en droit commercial",
    h1: "Trouver un avocat en droit commercial",
    seo_title: "Avocat droit commercial — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat en droit commercial (impayés, contrats, concurrence, distribution, baux commerciaux). Orientation institutionnelle CONFILEX.",
    intent: ["avocat commercial", "avocat impayés", "avocat contrat commercial"],
    cases: ["Recouvrement de créances", "Contrats commerciaux", "Concurrence déloyale", "Distribution / franchise", "Baux commerciaux", "Contentieux fournisseur"],
    docs: ["Contrat ou bon de commande", "Factures impayées", "Mises en demeure", "Courriels", "Conditions générales"],
  },
  "avocat-famille": {
    parent: "avocat",
    label: "Avocat en droit de la famille",
    h1: "Trouver un avocat en droit de la famille",
    seo_title: "Avocat droit de la famille — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat en droit de la famille (divorce, garde d'enfants, pension, succession). Orientation institutionnelle CONFILEX.",
    intent: ["avocat famille", "avocat divorce", "avocat pension alimentaire"],
    cases: ["Divorce amiable ou contentieux", "Garde d'enfants / résidence", "Pension alimentaire", "Succession contestée", "Liquidation de communauté", "Contrat de mariage / PACS"],
    docs: ["Contrat de mariage / PACS", "Jugements antérieurs", "Convention de divorce", "Justificatifs de revenus", "Inventaire patrimonial"],
  },
  "avocat-recouvrement": {
    parent: "avocat",
    label: "Avocat en recouvrement de créances",
    h1: "Trouver un avocat en recouvrement de créances",
    seo_title: "Avocat recouvrement — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat spécialisé en recouvrement amiable et judiciaire de créances. Orientation institutionnelle CONFILEX.",
    intent: ["avocat recouvrement", "avocat impayés", "avocat injonction de payer"],
    cases: ["Factures impayées clients", "Loyers impayés", "Injonction de payer", "Procédure de référé provision", "Saisie conservatoire", "Recouvrement transfrontalier"],
    docs: ["Contrats et bons de commande", "Factures impayées", "Mises en demeure", "Relances envoyées", "Justificatifs de livraison"],
  },
  "avocat-affaires": {
    parent: "avocat",
    label: "Avocat en droit des affaires",
    h1: "Trouver un avocat en droit des affaires",
    seo_title: "Avocat droit des affaires — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un avocat en droit des affaires (M&A, restructuration, due diligence, gouvernance). Orientation institutionnelle CONFILEX.",
    intent: ["avocat affaires", "avocat M&A", "avocat restructuration"],
    cases: ["Acquisition / cession de société", "Restructuration capitalistique", "Due diligence vendeur ou acquéreur", "Joint-venture", "Gouvernance et reporting", "Opérations transfrontalières"],
    docs: ["Statuts et K-bis", "Pacte d'associés", "Comptes annuels", "Lettre d'intention (LOI)", "Data room"],
  },

  // -- Experts-comptables (5)
  "expert-comptable-tpe": {
    parent: "expert-comptable",
    label: "Expert-comptable TPE",
    h1: "Trouver un expert-comptable pour TPE",
    seo_title: "Expert-comptable TPE — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable adapté à votre TPE (création, tenue, paie, déclarations). Orientation institutionnelle CONFILEX.",
    intent: ["expert-comptable TPE", "expert-comptable petite entreprise", "comptable artisan"],
    cases: ["Création d'entreprise / micro-BIC", "Tenue mensuelle ou trimestrielle", "Paie 1-9 salariés", "Déclarations TVA / IS / CFE", "Bilan annuel", "Conseil de pilotage"],
    docs: ["K-bis", "Statuts", "Relevés bancaires", "Factures émises/reçues", "Bulletins de paie"],
  },
  "expert-comptable-pme": {
    parent: "expert-comptable",
    label: "Expert-comptable PME",
    h1: "Trouver un expert-comptable pour PME",
    seo_title: "Expert-comptable PME — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable pour PME (consolidation, audit, paie multi-établissement, situations). Orientation institutionnelle CONFILEX.",
    intent: ["expert-comptable PME", "expert-comptable consolidation", "audit PME"],
    cases: ["Consolidation multi-entités", "Audit légal / contractuel", "Situations intermédiaires", "Paie multi-établissement", "Reporting de gestion", "Optimisation de structure"],
    docs: ["Comptes annuels", "Balance générale", "FEC", "Reporting de gestion", "Conventions intra-groupe"],
  },
  "expert-comptable-sci": {
    parent: "expert-comptable",
    label: "Expert-comptable SCI",
    h1: "Trouver un expert-comptable spécialisé SCI",
    seo_title: "Expert-comptable SCI — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable spécialisé en SCI (option IS, IR, comptes courants, déclarations 2072). Orientation institutionnelle CONFILEX.",
    intent: ["expert-comptable SCI", "comptabilité SCI", "SCI IS ou IR"],
    cases: ["Création SCI familiale ou patrimoniale", "Option IS vs IR", "Tenue comptable SCI", "Déclaration 2072", "Compte courant d'associé", "Cession de parts SCI"],
    docs: ["Statuts SCI", "Relevés bancaires", "Actes d'acquisition", "Tableaux d'emprunt", "Quittances de loyer"],
  },
  "expert-comptable-sas": {
    parent: "expert-comptable",
    label: "Expert-comptable SAS",
    h1: "Trouver un expert-comptable pour SAS",
    seo_title: "Expert-comptable SAS — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable adapté aux SAS (rémunération président, dividendes, BSPCE, levée de fonds). Orientation institutionnelle CONFILEX.",
    intent: ["expert-comptable SAS", "comptable SAS", "comptable startup"],
    cases: ["Constitution SAS / SASU", "Rémunération du président", "Politique de dividendes", "BSPCE / BSA / Stock-options", "Levée de fonds", "Reporting investisseurs"],
    docs: ["Statuts SAS", "PV de nomination", "Pacte d'associés", "Comptes annuels", "Reportings investisseurs"],
  },
  "expert-comptable-immobilier": {
    parent: "expert-comptable",
    label: "Expert-comptable immobilier",
    h1: "Trouver un expert-comptable spécialisé immobilier",
    seo_title: "Expert-comptable immobilier — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable spécialisé immobilier (LMNP, location meublée, SCI, déficit foncier, plus-value). Orientation institutionnelle CONFILEX.",
    intent: ["expert-comptable immobilier", "expert-comptable LMNP", "comptable location meublée"],
    cases: ["LMNP / LMP", "Location meublée saisonnière", "Déficit foncier", "Plus-value immobilière", "SCI à l'IS", "Holding immobilière"],
    docs: ["Acte d'acquisition", "Tableaux d'amortissement", "Quittances", "Factures de travaux", "Déclarations 2042 / 2044"],
  },
  "expert-comptable-holding": {
    parent: "expert-comptable",
    label: "Expert-comptable holding",
    h1: "Trouver un expert-comptable spécialisé holding",
    seo_title: "Expert-comptable holding — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable spécialisé holding (régime mère-fille, intégration fiscale, consolidation). Orientation institutionnelle CONFILEX.",
    intent: ["expert-comptable holding", "comptable holding", "intégration fiscale"],
    cases: ["Holding patrimoniale", "Holding animatrice", "Intégration fiscale", "Régime mère-fille", "Reporting groupe", "Conventions intra-groupe"],
    docs: ["Organigramme groupe", "Statuts holding et filles", "Comptes consolidés", "Conventions intra-groupe", "Pacte d'associés"],
  },
  "expert-comptable-startup": {
    parent: "expert-comptable",
    label: "Expert-comptable startup",
    h1: "Trouver un expert-comptable pour startup",
    seo_title: "Expert-comptable startup — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable adapté aux startups (BSPCE, levée de fonds, reporting investisseurs, CIR/JEI). Orientation CONFILEX.",
    intent: ["expert-comptable startup", "comptable startup", "BSPCE CIR JEI"],
    cases: ["Création startup / SAS", "BSPCE / BSA / Stock-options", "Levée de fonds (seed, série A)", "CIR / CII / JEI", "Reporting investisseurs", "Préparation due diligence"],
    docs: ["Statuts SAS", "Cap table", "Term sheet", "Comptes annuels", "Reporting investisseurs"],
  },
  "expert-comptable-lmnp": {
    parent: "expert-comptable",
    label: "Expert-comptable LMNP / LMP",
    h1: "Trouver un expert-comptable spécialisé LMNP",
    seo_title: "Expert-comptable LMNP — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un expert-comptable spécialisé LMNP / LMP (location meublée non professionnelle, amortissements, déclaration 2031).",
    intent: ["expert-comptable LMNP", "comptable LMP", "déclaration 2031 LMNP"],
    cases: ["Location meublée non professionnelle (LMNP)", "Location meublée professionnelle (LMP)", "Location saisonnière", "Amortissements LMNP", "Déclaration 2031", "Passage LMNP / LMP"],
    docs: ["Acte d'acquisition", "Tableaux d'amortissement", "Quittances de loyer", "Factures travaux et mobilier", "Comptes bancaires dédiés"],
  },

  // -- Notaires (4)
  "notaire-achat-immobilier": {
    parent: "notaire",
    label: "Notaire pour achat immobilier",
    h1: "Trouver un notaire pour un achat immobilier",
    seo_title: "Notaire achat immobilier — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un notaire pour votre acquisition (résidence principale, secondaire, investissement). Orientation institutionnelle CONFILEX.",
    intent: ["notaire achat immobilier", "notaire vente immobilière", "frais de notaire"],
    cases: ["Résidence principale", "Résidence secondaire", "Investissement locatif", "Achat en démembrement", "Achat en SCI", "Achat à l'étranger"],
    docs: ["Compromis de vente", "Diagnostics", "Justificatif d'apport", "Offre de prêt", "Pièces d'identité"],
  },
  "notaire-succession": {
    parent: "notaire",
    label: "Notaire succession",
    h1: "Trouver un notaire pour une succession",
    seo_title: "Notaire succession — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un notaire pour le règlement d'une succession (déclaration, partage, indivision). Orientation institutionnelle CONFILEX.",
    intent: ["notaire succession", "règlement succession", "partage succession"],
    cases: ["Déclaration de succession", "Partage amiable", "Indivision", "Donation-partage", "Succession internationale", "Renonciation à succession"],
    docs: ["Acte de décès", "Testament éventuel", "Livret de famille", "Inventaire patrimonial", "Comptes bancaires"],
  },
  "notaire-donation": {
    parent: "notaire",
    label: "Notaire donation",
    h1: "Trouver un notaire pour une donation",
    seo_title: "Notaire donation — Trouver un spécialiste · CONFILEX",
    seo_description: "Identifier un notaire pour une donation (entre vifs, donation-partage, démembrement). Orientation institutionnelle CONFILEX.",
    intent: ["notaire donation", "donation enfants", "donation-partage"],
    cases: ["Donation entre époux", "Donation aux enfants", "Donation-partage", "Démembrement de propriété", "Donation immobilière", "Donation de parts de société"],
    docs: ["Pièces d'identité", "Justificatifs patrimoniaux", "Statuts de société (si parts)", "Évaluation des biens"],
  },
  "notaire-investissement": {
    parent: "notaire",
    label: "Notaire investissement immobilier",
    h1: "Trouver un notaire pour un investissement immobilier",
    seo_title: "Notaire investissement immobilier — CONFILEX",
    seo_description: "Identifier un notaire pour structurer un investissement immobilier (SCI, holding, démembrement, Pinel, LMNP).",
    intent: ["notaire investissement", "notaire SCI", "notaire holding immobilière"],
    cases: ["Acquisition en SCI", "Holding immobilière", "Démembrement viager / temporaire", "Pinel / Denormandie", "Location meublée structurée", "Investissement à plusieurs"],
    docs: ["Statuts SCI envisagés", "Compromis", "Tableaux d'amortissement", "Étude patrimoniale"],
  },
  "notaire-transmission": {
    parent: "notaire",
    label: "Notaire transmission",
    h1: "Trouver un notaire pour une transmission patrimoniale",
    seo_title: "Notaire transmission patrimoniale — CONFILEX",
    seo_description: "Identifier un notaire pour préparer la transmission de votre patrimoine (donation-partage, démembrement, pacte Dutreil, holding).",
    intent: ["notaire transmission", "transmission patrimoine", "pacte Dutreil"],
    cases: ["Donation-partage", "Démembrement temporaire ou viager", "Transmission d'entreprise familiale", "Pacte Dutreil", "Holding patrimoniale", "Transmission progressive"],
    docs: ["Étude patrimoniale familiale", "Statuts de société (si parts)", "Comptes annuels", "Précédentes donations", "Inventaire patrimonial"],
  },
  "notaire-patrimoine": {
    parent: "notaire",
    label: "Notaire patrimoine",
    h1: "Trouver un notaire pour la gestion de patrimoine",
    seo_title: "Notaire gestion de patrimoine — CONFILEX",
    seo_description: "Identifier un notaire spécialisé en gestion de patrimoine (SCI, démembrement, donation, succession, contrat de mariage).",
    intent: ["notaire patrimoine", "notaire gestion patrimoine", "ingénierie patrimoniale"],
    cases: ["Stratégie patrimoniale globale", "SCI familiale ou patrimoniale", "Contrat de mariage", "Démembrement patrimonial", "Donation-partage", "Préparation de succession"],
    docs: ["Étude patrimoniale", "Pièces d'identité", "Contrat de mariage / PACS", "Inventaire patrimonial", "Statuts de sociétés patrimoniales"],
  },

  // -- Négociation / médiation (2)
  "negociation-amiable": {
    parent: "mediateur",
    label: "Négociation amiable",
    h1: "Trouver un médiateur pour une négociation amiable",
    seo_title: "Négociation amiable — Résolution de différend · CONFILEX",
    seo_description: "Identifier un médiateur pour résoudre amiablement un différend civil, commercial ou contractuel. Orientation CONFILEX.",
    intent: ["négociation amiable", "résolution amiable", "médiation", "accord amiable"],
    cases: ["Différend contractuel", "Conflit commercial B2B", "Litige fournisseur / client", "Conflit entre associés", "Différend de voisinage", "Conflit familial"],
    docs: ["Contrat ou commande", "Mises en demeure échangées", "Courriels", "Historique des relances", "Pièces probatoires"],
  },
  "negociation-creances": {
    parent: "mediateur",
    label: "Négociation de créances",
    h1: "Négocier une créance ou un étalement de dette",
    seo_title: "Négociation de créances — Étalement de dette · CONFILEX",
    seo_description: "Préparer un dossier structuré de négociation de créance ou d'étalement de dette avant transmission à un professionnel qualifié.",
    intent: ["négociation créances", "étalement de dette", "moratoire dette", "plan d'apurement"],
    cases: ["Étalement de dette fiscale", "Plan d'apurement URSSAF", "Négociation bancaire", "Délai de paiement commercial", "Plan d'apurement loyer", "Procédure de surendettement préparée"],
    docs: ["Tableau d'endettement", "Avis d'imposition", "Relevés bancaires", "Courriers de relance", "Justificatifs de revenus"],
  },
};


export default function SpecialtyPage({ slug }) {
  const cfg = SPECIALTY_PAGES[slug];
  if (!cfg) return <div className="p-20 text-center">Page non trouvée.</div>;

  const parentMap = {
    "avocat": { label: "Avocats", to: "/trouver-un-avocat", icon: Scale },
    "expert-comptable": { label: "Experts-comptables", to: "/trouver-un-expert-comptable", icon: Briefcase },
    "notaire": { label: "Notaires", to: "/trouver-un-notaire", icon: HomeIcon },
    "mediateur": { label: "Médiateurs", to: "/trouver-un-mediateur", icon: Users },
  };
  const parent = parentMap[cfg.parent];
  const ParentIcon = parent?.icon || BookOpen;

  // FAQ générique
  const faq = [
    {
      q: `Comment CONFILEX m'aide-t-il à trouver un ${cfg.label.toLowerCase()} ?`,
      a: `CONFILEX qualifie d'abord votre dossier puis vous oriente vers des profils dont la spécialisation correspond. Aucun répertoire librement consultable — l'accès passe toujours par une qualification documentaire préalable.`,
    },
    {
      q: "Quels documents préparer avant la consultation ?",
      a: `Les pièces utiles dépendent de votre dossier. Le Vérificateur CONFILEX peut produire une lecture documentaire factuelle de vos pièces (résumé, dates clés, points à clarifier) afin de transmettre au professionnel un dossier déjà structuré.`,
    },
    {
      q: "Qui paie le professionnel ?",
      a: `Les honoraires sont fixés librement entre vous et le professionnel. CONFILEX ne facture aucun honoraire pour le compte des professionnels et ne perçoit aucune commission sur ces honoraires.`,
    },
  ];

  return (
    <>
      <Seo
        title={cfg.seo_title}
        description={cfg.seo_description}
        canonical={`/${slug}`}
        keywords={cfg.intent}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": cfg.h1,
            "description": cfg.seo_description,
            "provider": { "@type": "Organization", "name": "CONFILEX", "url": "https://confilex.fr" },
            "areaServed": { "@type": "Country", "name": "France" },
            "serviceType": cfg.label,
          },
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            ...(parent ? [{ name: parent.label, url: parent.to }] : []),
            { name: cfg.h1, url: `/${slug}` },
          ]),
          ldFAQ(faq),
        ]}
      />

      <main className="bg-paper min-h-screen" data-testid={`specialty-${slug}`}>
        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              CONFILEX · {parent?.label.toUpperCase() || "ORIENTATION"}
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="page-h1">
              {cfg.h1}
            </h1>
            <p className="text-paper/75 text-base max-w-3xl leading-relaxed mb-6">
              CONFILEX vous aide à identifier le bon professionnel et à lui transmettre un dossier
              structuré, exploitable et déjà organisé. Vous gagnez en clarté, le professionnel gagne du temps.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/orientation-intelligente">
                <Button className="bg-copper text-paper hover:bg-copper/90 rounded-sm" data-testid="cta-orientation">
                  <Compass className="w-4 h-4 mr-2" /> Trouver un {cfg.label.toLowerCase()}
                </Button>
              </Link>
              <Link to="/verifier">
                <Button variant="outline" className="border-paper/40 text-paper hover:bg-paper/10 rounded-sm" data-testid="cta-verifier">
                  <ScanLine className="w-4 h-4 mr-2" /> Préparer mon dossier
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">SITUATIONS TYPIQUES</p>
          <h2 className="font-serif italic text-3xl text-night mb-6">Dans quels cas CONFILEX peut-il vous orienter ?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cfg.cases.map((c, i) => (
              <Card key={i} className="p-4 border-sage hover:border-copper transition-smooth" data-testid={`case-${i}`}>
                <ParentIcon className="w-4 h-4 text-copper mb-2" />
                <p className="text-sm font-medium text-night">{c}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="bg-ivory border-t border-sage">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">PRÉPARATION DU DOSSIER</p>
            <h2 className="font-serif italic text-3xl text-night mb-3">
              Transmettez un dossier déjà structuré
            </h2>
            <p className="text-sm text-night/75 mb-6 max-w-3xl">
              Un professionnel gagne un temps précieux quand votre dossier est préparé. Voici les pièces
              utiles que CONFILEX peut vous aider à organiser :
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {cfg.docs.map((d, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`doc-${i}`}>
                  <BadgeCheck className="w-4 h-4 text-copper mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-night/85">{d}</p>
                </div>
              ))}
            </div>
            <Link to="/verifier" className="inline-flex items-center text-sm text-copper hover:underline">
              Préparer mon dossier avec le Vérificateur <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">QUESTIONS FRÉQUENTES</p>
          <h2 className="font-serif italic text-3xl text-night mb-6">À savoir</h2>
          <div className="space-y-4">
            {faq.map((item, i) => (
              <Card key={i} className="p-5 border-sage">
                <h3 className="font-semibold text-night mb-2">{item.q}</h3>
                <p className="text-sm text-night/75 leading-relaxed">{item.a}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="bg-night text-paper border-t border-paper/10">
          <div className="max-w-5xl mx-auto px-6 py-10">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              EXPLORER D'AUTRES SPÉCIALITÉS
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(SPECIALTY_PAGES)
                .filter(([s, c]) => s !== slug && c.parent === cfg.parent)
                .slice(0, 5)
                .map(([s, c]) => (
                  <Link
                    key={s}
                    to={`/${s}`}
                    className="block p-3 border border-paper/20 rounded-sm hover:border-copper hover:bg-paper/5 transition-smooth"
                    data-testid={`related-${s}`}
                  >
                    <p className="text-sm font-medium">{c.label}</p>
                  </Link>
                ))}
              {parent && (
                <Link
                  to={parent.to}
                  className="block p-3 border border-copper/40 rounded-sm hover:bg-paper/5 transition-smooth"
                >
                  <p className="text-sm font-medium">Voir toutes les spécialités {parent.label.toLowerCase()}</p>
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="border-t border-sage bg-paper">
          <div className="max-w-5xl mx-auto px-6 py-8 flex items-start gap-3 text-sm text-night/75">
            <ShieldCheck className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
            <p>
              CONFILEX n'est pas un cabinet d'avocats, d'experts-comptables, de notaires ni
              de conseil fiscal. Les professionnels référencés exercent en toute indépendance.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
